<template>
    <div class="page-container">
        <Headbar>
            <template v-slot:left>
                <Button className="--primary --small" v-if="$store.getters.hasPermission('store issues')"
                        :onclick="toggleCreate">
                    {{ $t('issues.report_an_issue') }}
                </Button>
            </template>
            <template v-slot:right>
                <FormInputSelect v-model="selectedVehicle" identifier="vehicle" :options="vehicles" class="select-margin-fix filter-desktop"
                                 :placeholder="$t('bookings.license_plate')" :disabled="is_loading_vehicles" @updated="onFilterUpdated"
                                 track-by="id"  :display-custom-label="(row) => `${row.attributes.license_plate} - ${row.relationships.brand.data.attributes.name} - ${row.attributes.model}`"/>

                <Search class="search-desktop" :placeholder="$t('issues.search_issues')" @search="search"/>
                <button class="btn-search-mobile btn-icon-only"
                        @click="headbarExpanded = headbarExpanded === 'search' ? null : 'search'">
                    <font-awesome-icon v-if="headbarExpanded === 'search'" :icon="['far', 'times']"/>
                    <font-awesome-icon v-else :icon="['far', 'search']"/>
                </button>
            </template>

            <template v-slot:expanded>
                <div class="headbar-expanded-container">
                    <Search v-if="headbarExpanded === 'search'" :placeholder="$t('issues.search_issues')" @search="search"/>
                    <FormInputSelect v-model="selectedVehicle" identifier="vehicle" :options="vehicles" class="select-margin-fix mobile-margin-fix"
                                     :placeholder="$t('bookings.license_plate')" :disabled="is_loading_vehicles" @updated="onFilterUpdated"
                                     track-by="id"  :display-custom-label="(row) => `${row.attributes.license_plate} - ${row.relationships.brand.data.attributes.name} - ${row.attributes.model}`"
                                     v-if="headbarExpanded === 'search'"/>
                </div>
            </template>
        </Headbar>
        <main>
            <vue-good-table
                mode="remote"
                styleClass="vgt-table vgt-custom"
                :columns="columns"
                :rows="issues"
                :isLoading.sync="is_loading_issues"
                :search-options="{
                    enabled: false,
                }"
                :pagination-options="{
                    enabled: true,
                    mode: 'records',
                    dropdownAllowAll: false,
                    perPage: 15,
                    perPageDropdownEnabled: false,
                    rowsPerPageLabel: $t('x_per_page', {x: $t('issues.issues')}),
                }"
                :sort-options="{
                  enabled: true,
                  multipleColumns: true,
                }"
                :totalRows="totalRecords"
                @on-page-change="onPageChange"
                @on-sort-change="onSortChange">
                <template slot="table-row" slot-scope="props">
                    <div v-if="props.column.field === 'role'">
                        <p style="text-transform: capitalize;">{{ props.row.relationships.roles.data[0].attributes.name }}</p>
                    </div>
                    <div v-else-if="props.column.field === 'attributes.reported_by'">
                        <p style="text-transform: capitalize;">{{ `${props.row.relationships.user.data.attributes.name}` }}</p>
                    </div>
                    <div v-else-if="props.column.field === 'attributes.license_plate'">
                        <p style="text-transform: capitalize;">{{ `${props.row.relationships.vehicle.data.attributes.license_plate}` }}</p>
                    </div>
                    <div v-else-if="props.column.field === 'attributes.comment'" class="text-overflow-fix">
                        <p style="text-transform: capitalize;">{{ `${props.row.attributes.comment}` }}</p>
                    </div>
                    <div v-else-if="props.column.field === 'attributes.status'">
                        <p style="text-transform: capitalize;">{{ props.row.attributes.status }}</p>
                    </div>
                    <div v-else-if="props.column.field === 'after'" class="td-after">
                        <Button
                            v-if="$store.getters.hasPermission('read issues')"
                            className="--secondary --outline --mini --big-text"
                            :onclick="()=>toggleView(props.row)">
                            <font-awesome-icon :icon="['fal', 'eye']"/>
                        </Button>

                        <Button
                            v-if="$store.getters.hasAnyPermission(['read issues', 'update issues'])"
                            className="--secondary --outline --mini --big-text"
                            :onclick="()=>toggleUpdate(props.row)">
                            <font-awesome-icon :icon="['fal', 'pencil']"/>
                        </Button>

                        <Button
                            v-if="$store.getters.hasPermission('destroy issues')"
                            className="--secondary --outline --mini --big-text"
                            :onclick="()=>toggleDelete(props.row)">
                            <font-awesome-icon :icon="['fal', 'trash']"/>
                        </Button>

                    </div>
                    <span v-else>
                      {{ props.formattedRow[props.column.field] }}
                    </span>
                </template>
            </vue-good-table>
        </main>
    </div>
</template>

<script>
import Headbar from "../../components/headbar/Headbar";
import Search from "../../components/Search";
import Button from "../../components/Button";
import Select from "../../components/form/Select";
import ConfirmModal from "../../components/modal/ConfirmModal";
import IssuesCreateModal from "../../components/issues/IssuesCreateModal";
import IssuesUpdateModal from "../../components/issues/IssuesUpdateModal";
import IssuesViewModal from "../../components/issues/IssuesViewModal";
import FormInputSelect from "../../components/form/FormInputSelect";

export default {
    name: "issues-index-page",
    components: {ConfirmModal, Select, Button, Search, Headbar, FormInputSelect},
    data: function () {
        const columns = [
            {
                label: this.$t('issues.id'),
                field: 'id',
                sortable: false,
                width: '20%',
            },
            {
                label: this.$t('issues.license_plate'),
                field: 'attributes.license_plate',
                sortable: false,
                width: '20%',
            },
            {
                label: this.$t('issues.reported_by'),
                field: 'attributes.reported_by',
                sortable: false,
                width: '20%',
            },

            {
                label: this.$t('issues.status'),
                field: 'attributes.status',
                sortable: false,
                width: '20%',
            },
            {
                label: this.$t('issues.comment'),
                field: 'attributes.comment',
                sortable: false,
                width: '20%',
            },
        ];

        if (this.$store.getters.hasAnyPermission(['update issues', 'destroy issues']))
            columns.push({
                label: this.$t('actions'),
                field: 'after',
                tdClass: 'td-after',
                sortable: false,
                width: '1%',
            });

        return {
            columns: columns,
            issues: [],
            vehicles: [],
            is_loading_issues: false,
            is_loading_vehicles: false,
            selectedVehicle: null,
            totalRecords: null,
            serverParams: {sorting: []},
            searchTerm: null,
            is_deleting: false,
            headbarExpanded: false
        }
    },
    methods: {
        toggleDelete(issue) {
            this.$modal.show(
                ConfirmModal, {
                    title: this.$t('issues.delete_issue'),
                    message: this.$t('issues.prompt_delete'),
                    confirmText: this.$t('delete'),
                    cancelText: this.$t('cancel'),
                    confirmClass: '--primary',
                    cancelClass: '--secondary --outline'
                },
                {
                    name: 'confirm-modal',
                    adaptive: true,
                    resizable: true,
                    height: 'auto',
                    scrollable: true,
                    classes: 'modal',
                }, {
                    'before-close': (e) => {
                        if (e.params === true) {
                            this.is_deleting = true;
                            this.$axios.delete(`issues/${issue.id}`)
                                .then(({data}) => {
                                    this.is_deleting = false;
                                    this.retrieveIssues();
                                })
                                .catch(e => {
                                    this.is_deleting = false;

                                    this.$notify({
                                        title: this.$t('error'),
                                        text: this.$larerror(e.response.data, this.$t('issues.error_delete')),
                                        type: 'error',
                                    });
                                });
                        }
                    }
                }
            );
        },

        toggleCreate() {
            this.$modal.show(
                IssuesCreateModal, {},
                {
                    name: 'issues-create-modal',
                    adaptive: true,
                    resizable: true,
                    height: 'auto',
                    scrollable: true,
                    classes: 'modal',
                }, {
                    'before-close': (e) => {
                        if (e.params === true)
                            this.retrieveIssues();
                    }
                }
            );
        },

        toggleUpdate(issue) {
            this.$modal.show(
                IssuesUpdateModal,
                {
                    issue_id: issue.id,
                },
                {
                    name: 'issues-update-modal',
                    adaptive: true,
                    resizable: true,
                    height: 'auto',
                    scrollable: true,
                    classes: 'modal',
                }, {
                    'before-close': (e) => {
                        if (e.params === true)
                            this.retrieveIssues();
                    }
                }
            );
        },

        toggleView(issue) {
            this.$modal.show(
                IssuesViewModal,
                {
                    issue_id: issue.id,
                },
                {
                    name: 'issues-view-modal',
                    adaptive: true,
                    resizable: true,
                    height: 'auto',
                    scrollable: true,
                    classes: 'modal',
                }, {
                    'before-close': (e) => {
                        if (e.params === true){

                        }
                    }
                }
            );
        },

        onFilterUpdated() {
            const filters = [];

            if (this.selectedVehicle) {
                filters.push({
                    filter_by: 'vehicle_id',
                    filter_value: this.selectedVehicle.id
                })
            }

            if (filters.length)
                this.updateParams({filters: filters})
            else
                this.removeParam('filters');

            this.retrieveIssues();
        },

        updateParams(newProps) {
            this.serverParams = Object.assign({}, this.serverParams, newProps);
        },
        onPageChange(params) {
            this.updateParams({page: params.currentPage});
            this.retrieveIssues();
        },
        onSortChange(params) {
            const sorts = [];

            if (!params || !params.length) {
                this.updateParams({sorting: []});
                return this.retrieveIssues();
            }

            params.forEach(p => {
                if (!p.type || p.type === 'none')
                    return;

                let sort_by = null;
                let sort_order = p.type;
                if (p.field === 'attributes.name')
                    sort_by = 'name';
                else if (p.field === 'attributes.email')
                    sort_by = 'email';
                else if (p.field === 'role')
                    sort_by = 'role';
                else
                    sort_by = p.field.split('.')[1];

                sorts.push({sort_order, sort_by})
            });

            this.updateParams({sorting: sorts});
            this.retrieveIssues();
        },
        removeParam(param) {
            this.$delete(this.serverParams, param);
        },
        search(searchTerm) {
            this.searchTerm = searchTerm;

            if (searchTerm && searchTerm.length)
                this.updateParams({term: searchTerm});
            else this.removeParam('term');

            this.retrieveIssues();
        },

        retrieveVehicles() {
            this.is_loading_vehicles = true;

            this.$axios.get('vehicles/list')
                .then(({data}) => {
                    this.vehicles = data.data;
                    this.is_loading_vehicles = false;
                })
                .catch(e => {
                    this.is_loading_vehicles = false;

                    this.$notify({
                        title: this.$t('error'),
                        text: this.$larerror(e.response.data, this.$t('vehicles.error_retrieve')),
                        type: 'error',
                    });
                });
        },

        retrieveIssues() {
            this.is_loading_issues = true;

            const encodedSorting = this.serverParams.sorting.map(f => btoa(JSON.stringify(f)));

            this.$axios.get('issues', {params: {...this.serverParams, sorting: encodedSorting}})
                .then(({data}) => {
                    this.issues = data.data;
                    this.totalRecords = data.meta.total;
                    this.is_loading_issues = false;
                })
                .catch(e => {
                    this.is_loading_issues = false;

                    this.$notify({
                        title: this.$t('error'),
                        text: this.$larerror(e.response.data, this.$t('issues.error_retrieve')),
                        type: 'error',
                    });
                });
        }
    },

    mounted() {
        this.retrieveVehicles();
        this.retrieveIssues();
    },

    head() {
        return {
            title: {
                inner: this.$t('nav.issues')
            },
        }
    }
}
</script>

<style lang="scss" scoped>
.page-container {
    .headbar-expanded-container {
        @apply mt-4 flex flex-col gap-y-4 items-center;

        @screen md {
            @apply hidden;
        }
    }

    .select-margin-fix{
        margin-bottom: 0 !important;
        width: 150px !important;
        height: 50px !important;
        min-height: 50px !important;


        &.mobile-margin-fix{
            @apply mx-auto;
            width: 241px !important;
            padding-right: 4px;
        }
    }

    .select-dropdown {
        @apply min-w-40;
    }

    .text-overflow-fix{
        width: 12rem;

        p{
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }

    .btn-search-mobile, .btn-filter-mobile {
        @apply block text-primary w-6;

        @screen md {
            @apply hidden;
        }

        &:active, &:focus {
            @apply outline-none;
        }
    }

    .search-desktop, .filter-desktop {
        @apply hidden;

        @screen md {
            @apply block;
        }
    }

    .search-container {
        @apply mx-auto;

        @screen md{
            @apply ml-2;
        }

    }

    .filter-mobile {
        @apply max-w-xs;
    }

    .td-after {
        @apply flex flex-row;

        & > * {
            @apply mr-3;

            &:last-child {
                @apply mr-0;
            }
        }
    }
}
</style>
